// =============================================================================
// ui/tabs/MidSessionNotes.tsx — Session → Mid-Session Notes.
// -----------------------------------------------------------------------------
// The running log: a timestamped one-liner dropped in while the session is open.
// Read-only until now, which made the Session phase a page with nothing to do on
// it — the notes are the reason a trader opens this tab at 10:42.
//
// ── ⚠ EVERY EDIT SAVES IMMEDIATELY. NO SAVE BUTTON, DELIBERATELY ──
//
// The other journal surfaces are forms: a draft you shape and then commit. This
// is a log. A note held in an unsaved draft while the trader switches to the
// chart is a note that never existed, and there is no partial state worth
// protecting — one line, in or out.
//
// So Add writes, and Remove writes. Both are guarded the same way as every other
// journal write: if the day changed elsewhere since it was loaded, nothing is
// sent and the message says so.
//
// ── ⚠ REMOVE ASKS FIRST ──
//
// It is the only destructive control on the Session phase, the row is one line
// tall, and there is no undo. Beta deletes on the first click; that is beta being
// wrong rather than a convention worth porting.
//
// ⚠ The time is stamped by `stampNote` from a clock passed in, per D26 — the
// rule lives in `domain/`, and `new Date()` is this component's business.
// =============================================================================

import { useState } from 'react';
import { useAutosave } from '../state/useAutosave';
import { useSupabase } from '../state/supabase';
import { saveMidSessionNotes } from '../../io/sync/saveMidSessionNotes';
import { stampNote } from '../../selectors/journal';
import type { MidSessionNote } from '../../selectors/journal';

export interface MidSessionNotesProps {
  notes: readonly MidSessionNote[];
  date: string;
  baselineUpdatedAt: string | null;
  onSaved: (notes: MidSessionNote[], updatedAt: string | null) => void;
}

export function MidSessionNotes(
  { notes, date, baselineUpdatedAt, onSaved }: MidSessionNotesProps,
) {
  const { sb, userId } = useSupabase();
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const write = async (next: MidSessionNote[]): Promise<boolean> => {
    setBusy(true);
    setError(null);
    const r = await saveMidSessionNotes({
      sb, userId, date, notes: next, baselineUpdatedAt,
    });
    setBusy(false);
    if (r.error) { setError(r.error); return false; }
    onSaved(next, r.updatedAt);
    return true;
  };

  const add = async () => {
    const text = draft.trim();
    if (!text) return;
    // ⚠ The box is cleared only AFTER the write lands. Clearing first loses the
    // trader's sentence when the save is held stale — and "held stale" is exactly
    // the case where they need to be able to re-send it.
    const ok = await write([...notes, stampNote(text, new Date())]);
    if (ok) setDraft('');
  };

  const remove = async (id: string) => {
    setConfirmRemove(null);
    await write(notes.filter((n) => n.id !== id));
  };

  // ── 🔴 CORRECTING A NOTE (TCR-87) ──
  //
  // Josh: *"Comments on the session tab in the journal should be editable so
  // typos or rephrasing can occur."* Until now this surface offered Add and
  // Remove only, so a typo was permanent.
  //
  // 🔴 AN EDIT CHANGES `text` AND NOTHING ELSE. `stampNote` builds BOTH the id
  // and the time from the clock, so reaching for it here would re-stamp the note
  // to now — scrambling the chronology of a timestamped running log, which is the
  // entire point of it, and breaking `remove`, which filters by id. The spread
  // below is deliberate and is the whole safety property.
  const editing = editingId === null
    ? null
    : notes.find((n) => n.id === editingId) ?? null;

  // ⚠ Blank or unchanged is NOT dirty, and both matter. A trader who opens a note
  // to read it must not rewrite the whole day's notes on the way out, and an
  // emptied box must not become a delete — Remove is a separate, confirmed
  // action, and writing a blank here would route around its confirmation.
  const edited = editText.trim();
  const dirty = !!editing && edited.length > 0 && edited !== editing.text;

  const commitEdit = async () => {
    if (!editing || !dirty) { setEditingId(null); return; }
    const ok = await write(
      notes.map((n) => (n.id === editing.id ? { ...n, text: edited } : n)),
    );
    if (ok) setEditingId(null);
  };

  // ⚠ Commit on blur, and flush before the screen goes — leaving the tab destroys
  // this component (D94), and a correction typed and then abandoned by navigation
  // is the defect TCR-79 and TCR-80 exist to stop. The hook is the one they built.
  const autosave = useAutosave({ dirty, commit: commitEdit });

  return (
    <>
      {notes.length === 0 ? (
        <p className="muted">No mid-session notes.</p>
      ) : (
        <div className="ms-notes">
          {notes.map((n) => (
            <div className="ms-note" key={n.id}>
              <span className="mono muted ms-time">{n.time}</span>
              {editingId === n.id ? (
                <input
                  className="grow ms-text ms-edit"
                  aria-label={`Edit the note from ${n.time || 'this session'}`}
                  value={editText} disabled={busy} autoFocus
                  onChange={(e) => setEditText(e.target.value)}
                  /* ⚠ DIRTY WRITES, CLEAN JUST CLOSES — and the split matters in
                     both directions. `flush` is a no-op when nothing changed, so
                     hanging it on blur alone left the box open forever on an
                     unchanged or emptied edit, reading as if the note had been
                     erased. And on a FAILED write the box must STAY open: closing
                     it would drop the correction the trader just made, which is
                     the whole class of defect TCR-79/80 exist to stop. So only
                     `commitEdit` closes after a write, and only on success. */
                  onBlur={() => { if (dirty) autosave.flush(); else setEditingId(null); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (dirty) autosave.flush(); else setEditingId(null);
                    }
                    // ⚠ ESCAPE PUTS THE STORED TEXT BACK BEFORE CLOSING, and that
                    // ordering is the guard. A blur landing after the box goes
                    // then sees nothing to write, with no flag to keep in sync.
                    if (e.key === 'Escape') { setEditText(n.text); setEditingId(null); }
                  }}
                />
              ) : (
                /* ⚠ The TEXT is the control, rather than a pencil beside the ×.
                   That × is the only destructive thing on this phase and the row
                   is one line tall — another button next to it is a misclick
                   waiting to happen. */
                <button
                  type="button" className="grow ms-text ms-text-btn" disabled={busy}
                  aria-label={`Edit the note from ${n.time || 'this session'}`}
                  onClick={() => { setEditText(n.text); setEditingId(n.id); }}
                >{n.text}</button>
              )}
              {confirmRemove === n.id ? (
                <>
                  <span className="muted ms-confirm">Remove this note?</span>
                  <button type="button" className="btn btn-sm" disabled={busy}
                    onClick={() => setConfirmRemove(null)}>Cancel</button>
                  <button type="button" className="btn danger btn-sm" disabled={busy}
                    onClick={() => void remove(n.id)}>Remove</button>
                </>
              ) : (
                <button
                  type="button" className="btn danger btn-sm" disabled={busy}
                  aria-label={`Remove the note from ${n.time || 'this session'}`}
                  onClick={() => setConfirmRemove(n.id)}
                >×</button>
              )}
            </div>
          ))}
        </div>
      )}

      {error ? <p className="red" role="alert">{error}</p> : null}

      <div className="row gap-8 ms-add">
        <input
          aria-label="New mid-session note"
          placeholder="Quick observation, then Enter…"
          value={draft} disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
        />
        <button type="button" className="btn primary" disabled={busy || !draft.trim()}
          onClick={() => void add()}>
          {busy ? 'Saving…' : 'Add Note'}
        </button>
      </div>
    </>
  );
}
